<template>
  <div>
    <mds-notification-container v-if="showAlert" class="alert-box">
      <mds-notification
        key="sizing-medium"
        size="medium"
        variation="informational"
        :dismiss-delay="dismissVal"
        title="Reporting Concerns"
        role="alert"
        @mds-notification-dismissed="closePopup"
      >
        If an Authorized User experiences difficulties accessing the transaction
        information on this website, or has concerns about the completeness or
        timeliness of information relating to transactions on the 17g5 list,
        please submit the concerns in writing to the Morningstar DBRS Designated
        Compliance Officer at
        <a href="mailto:CreditCompliance@morningstar.com" class="my-link">
          CreditCompliance@morningstar.com
        </a>
        <template #mds-notification-custom-action>
          <mds-button
            type="button"
            variation="secondary"
            text="Send Email"
            @click.prevent="sendEmail"
          />
        </template>
      </mds-notification>
    </mds-notification-container>

    <div class="container_2">
      <div class="mds-level-6-heading">
        <span>Disclosure Deal List</span>
        <div class="reporting_concern">
          <span>Reporting Concerns</span>
          <span class="fix_icon" @click.prevent="openPopup()">
            <mds-icon name="info-circle" title="favorite" size="small" />
          </span>
        </div>
      </div>

      <mds-loader
        v-if="loading"
        class="loader_container"
        aria-valuetext="loading ..."
      />

      <div class="table-wrap">
        <table class="mds-data-table__table__mcr-rd">
          <caption class="mds-data-table__caption__mcr-rd" />
          <thead>
            <tr class="mds-data-table__row__mcr-rd">
              <th
                v-for="headerName in headers"
                :key="headerName['index']"
                scope="col"
                class="mds-data-table__header-cell__mcr-rd"
                :style="{ width: headerName['width'] }"
              >
                <div class="mds-data-table__header-cell-inner__mcr-rd">
                  <span class="mds-data-table__header-cell-text__mcr-rd">{{
                    headerName["text"]
                  }}</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="mds-data-table__body__mcr-rd">
            <tr
              v-for="data in rows"
              :key="data['id']"
              class="mds-data-table__row__mcr-rd"
            >
              <td class="mds-data-table__cell__mcr-rd">
                <span>{{ getDate(data.start_date) }}</span>
              </td>
              <td class="mds-data-table__cell__mcr-rd">
                <span>{{ data.issuer_id.name }}</span>
              </td>
              <td class="mds-data-table__cell__mcr-rd">
                <span>{{ data.transaction_name }}</span>
              </td>
              <td class="mds-data-table__cell__mcr-rd">
                <span>{{ data.instrument_id.name }}</span>
              </td>
              <td class="mds-data-table__cell__mcr-rd">
                <span>
                  <a
                    :href="data.url"
                    target="_blank"
                    class="table-url-link"
                    @click="urlclicked(data.url)"
                    >{{ data.url }}</a
                  >
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance } from "vue";
import { useRoute } from "vue-router";
import MdsIcon from "@mds/icon-vue3";
import {
  MdsNotification,
  MdsNotificationContainer,
} from "@mds/notification-vue3";
import { MdsButton } from "@mds/button-vue3";
import MdsLoader from "@mds/loader-vue3";
import { getDealList } from "@/apis/deals.js";
import { me } from "@/apis/user-authentication";
import { orderBy } from "lodash";
import moment from "moment";

// Reactive state
const headers = ref([]);
const showAlert = ref(false);
const dismissVal = ref(500000);
const rows = ref([]);
const loading = ref(false);
const userData = ref({});

// Composables
const route = useRoute();
const { proxy } = getCurrentInstance();
const $gtag = proxy.$gtag;

// Lifecycle
onMounted(async () => {
  userData.value = JSON.parse(localStorage.getItem("userData") || "{}");
  if (route.path !== "/" && !userData.value.id) {
    await userInfo();
  } else {
    await getDeals();
  }
  initData();
});

// Methods
const openPopup = () => {
  showAlert.value = true;
};

const closePopup = () => {
  showAlert.value = false;
};

const sendEmail = () => {
  window.location.href = "mailto:CreditCompliance@morningstar.com";
  closePopup();
};

const getDate = (date) => {
  return moment(date).format("YYYY-MM-DD");
};

const initData = () => {
  headers.value = [
    {
      fieldName: "initiated_date",
      text: "Date Initiated",
      width: "14.5%",
      index: 1,
    },
    {
      fieldName: "issuer_name",
      text: "Issuer",
      width: "36.2%",
      index: 2,
    },
    {
      fieldName: "transaction_name",
      text: "Transaction",
      width: "47.5%",
      index: 3,
    },
    {
      fieldName: "instrument_name",
      text: "Instrument",
      width: "24%",
      index: 4,
    },
    {
      fieldName: "url",
      text: "URL",
      width: "48%",
      index: 5,
    },
  ];
};

const getDeals = async () => {
  loading.value = true;
  try {
    const resp = await getDealList();
    rows.value = orderBy(
      resp.data,
      (x) => new Date(x.start_date).getTime(),
      "desc",
    );
  } catch (err) {
    handleError(err);
  } finally {
    loading.value = false;
  }
};

const handleError = (err) => {
  loading.value = false;
  console.error("Error:", err);
};

const urlclicked = (url) => {
  try {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    $gtag.customMap({
      dimension2: "username",
      dimension3: "email",
      dimension4: "url",
    });
    $gtag.event("view_item", {
      event_category: "view_link",
      event_label: "link clicked",
      value: user.id,
      username: user.username,
      email: user.email,
      url: url,
    });
  } catch (error) {
    console.error("Analytics error:", error);
  }
};

const userInfo = async () => {
  loading.value = true;
  try {
    const data = await me();
    if (data?.data) {
      await getDeals();
      $gtag.set({ userId: data.data.id });
      localStorage.setItem("userData", JSON.stringify(data.data));
      $gtag.customMap({ dimension2: "username", dimension3: "email" });
      $gtag.event("login", {
        event_category: "engagement",
        event_label: "user login",
        value: data.data.id,
        username: data.data.username,
        email: data.data.email,
      });
    }
  } catch (error) {
    handleError(error);
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/constants";
@import "@/styles/reset";

.container_2 {
  width: 1316px;
  margin-left: auto;
  margin-right: auto;
  height: 550px;
}

.mds-level-6-heading {
  @include mds-level-3-heading();
  margin-bottom: $mds-space-3-x;
  margin-top: $mds-space-3-x;
}

.reporting_concern {
  float: right;
  margin-top: 10px;
  @include mds-body-text-s();

  span {
    display: inline-block;
    margin-left: 2px;
  }

  .fix_icon {
    cursor: pointer;
    vertical-align: middle;
  }
}

.alert-box {
  margin-top: $mds-masthead-height + $mds-space-3-x;

  .my-link {
    @include mds-link($underline: false, $visited-styles: false);
    color: #007cc2;
  }
}

.mds-notification-container--top-center__mcr-rd {
  margin-left: auto;
  margin-right: auto;
}

.mds-notification-container__mcr-rd {
  max-width: 600px;
}

.mds-data-table__cell__mcr-rd span a:visited {
  color: #007cc2 !important;
}

.table-url-link {
  @include mds-link($underline: false, $visited-styles: false);
  color: #007cc2;
}

.loader_container {
  margin-left: auto;
  margin-right: auto;
  font-size: 40px !important;
  top: 40%;
}
</style>
